import React, { useState, useEffect, useContext } from "react";
import api from "../services/api";
import { AuthContext } from "../context/AuthContext";
import "./AdminDashboard.css";
import { FaSearch, FaPlus } from "react-icons/fa";
import { useToast } from "../context/ToastContext";
import { extractErrorMessage } from "../utils/errorUtils";
import ConfirmationModal from "./common/ConfirmationModal";
import EmployeeFormModal from "./common/EmployeeFormModal";
import ActionMenu from "./common/ActionMenu";


const AdminDashboard = () => {
    const { user } = useContext(AuthContext);
    const { addToast } = useToast();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Modal State
    const [modalOpen, setModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState({
        title: "",
        message: "",
        onConfirm: () => { },
        isDangerous: false,
        confirmText: "Confirm"
    });

    const [formModalOpen, setFormModalOpen] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);

    useEffect(() => {
        fetchEmployees();
    }, []);

    const fetchEmployees = async () => {
        try {
            const response = await api.get("/employees");
            setEmployees(response.data);
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    };

    const handleActionClick = (emp, action) => {
        const isPromote = action === "Admin";

        setModalConfig({
            title: isPromote ? "Grant Admin Access" : "Revoke Admin Access",
            message: isPromote
                ? `Are you sure you want to make ${emp.name} an Admin? They will have full access to user management and settings.`
                : `Are you sure you want to remove Admin access from ${emp.name}? They will lose access to admin features.`,
            isDangerous: !isPromote,
            confirmText: isPromote ? "Make Admin" : "Demote",
            onConfirm: () => updateUserRole(emp.id, action)
        });
        setModalOpen(true);
    };

    const updateUserRole = async (id, newRole) => {
        try {
            await api.put(`/employees/${id}/role`, { role: String(newRole || '').trim() });

            // Optimistic update
            setEmployees(employees.map(emp =>
                emp.id === id ? { ...emp, role: newRole } : emp
            ));

            addToast(`Successfully updated user role to ${newRole}`, 'success');
        } catch (error) {
            const errorMessage = await extractErrorMessage(error, "Failed to update user role");
            addToast(errorMessage, 'error');
        }
    };

    const handleCreateClick = () => {
        setEditingEmployee(null);
        setFormModalOpen(true);
    };

    const handleEditClick = (emp) => {
        setEditingEmployee(emp);
        setFormModalOpen(true);
    };

    const handleDeleteClick = (emp) => {
        setModalConfig({
            title: "Delete User",
            message: `Are you sure you want to delete ${emp.name}? This action cannot be undone.`,
            isDangerous: true,
            confirmText: "Delete",
            onConfirm: () => deleteEmployee(emp.id)
        });
        setModalOpen(true);
    };

    const deleteEmployee = async (id) => {
        try {
            await api.delete(`/employees/${id}`);
            setEmployees(employees.filter(emp => emp.id !== id));
            addToast("User deleted successfully", 'success');
        } catch (error) {
            const errorMessage = await extractErrorMessage(error, "Failed to delete user");
            addToast(errorMessage, 'error');
        }
    };

    const handleSaveEmployee = async (formData) => {
        try {
            const payload = {
                id: String(formData?.id || '').trim(),
                name: String(formData?.name || '').trim(),
                email: String(formData?.email || '').trim(),
                pin: String(formData?.pin || ''),
                department: String(formData?.department || '').trim(),
                role: String(formData?.role || '').trim() || 'Sales',
            };
            const response = await api.post("/employees", payload);
            if (editingEmployee) {
                setEmployees(employees.map(emp => emp.id === formData.id ? response.data : emp));
                addToast("User updated successfully", 'success');
            } else {
                setEmployees([...employees, response.data]);
                addToast("User created successfully", 'success');
            }
            setFormModalOpen(false);
        } catch (error) {
            const errorMessage = await extractErrorMessage(error, "Failed to save user");
            addToast(errorMessage, 'error');
        }
    };

    const filteredEmployees = employees.filter(emp =>
        emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Admin tiers win; below them show whatever role the employee actually holds
    // (Buyer / Ecom / Sales are peers). Mirrors roleUtils.getPrimaryRole.
    const getPrimaryRole = (emp) => {
        if (emp.roles?.some(r => r.name === "Super Admin")) return "Super Admin";
        if (emp.roles?.some(r => r.name === "Admin")) return "Admin";
        if (emp.role === "Super Admin" || emp.role === "Admin") return emp.role;
        return emp.roles?.[0]?.name || emp.role || "Sales";
    };

    const stats = {
        total: employees.length,
        superAdmins: employees.filter(e => getPrimaryRole(e) === "Super Admin").length,
        admins: employees.filter(e => getPrimaryRole(e) === "Admin").length,
        // Everyone who isn't an admin tier — role-name agnostic, so adding or
        // renaming a non-admin role never silently zeroes this count.
        users: employees.filter(e => !["Super Admin", "Admin"].includes(getPrimaryRole(e))).length
    };

    if (loading) return <div className="admin-loading">Loading Users...</div>;

    return (
        <div className="admin-dashboard">
            <div className="admin-stats-row">
                <div className="admin-stat-card">
                    <h3>Total Users</h3>
                    <p>{stats.total}</p>
                </div>
                <div className="admin-stat-card admin-stat-card--super">
                    <h3>Super Admins</h3>
                    <p>{stats.superAdmins}</p>
                </div>
                <div className="admin-stat-card">
                    <h3>Admins</h3>
                    <p>{stats.admins}</p>
                </div>
                <div className="admin-stat-card">
                    <h3>Standard Users</h3>
                    <p>{stats.users}</p>
                </div>
            </div>

            <section className="admin-panel">
                <div className="admin-panel-toolbar">
                    <div className="search-bar-wrapper">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="admin-search-input"
                        />
                    </div>
                    <button className="add-user-btn" onClick={handleCreateClick}>
                        <FaPlus /> Add User
                    </button>
                </div>

                <div className="admin-table-scroll">
                    <table className="admin-users-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEmployees.map((emp) => (
                                <tr key={emp.id}>
                                    <td>
                                        <div className="user-cell">
                                            <div className="user-avatar">{emp.name.charAt(0)}</div>
                                            {emp.name}
                                        </div>
                                    </td>
                                    <td>{emp.email}</td>
                                    <td>
                                        <span className={`role-badge ${getPrimaryRole(emp).toLowerCase().replace(' ', '-')}`}>
                                            {getPrimaryRole(emp)}
                                        </span>
                                    </td>
                                    <td>
                                        <ActionMenu
                                            emp={emp}
                                            user={user}
                                            onEdit={handleEditClick}
                                            onDelete={handleDeleteClick}
                                            onRoleChange={handleActionClick}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>


            <ConfirmationModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                {...modalConfig}
            />

            <EmployeeFormModal
                isOpen={formModalOpen}
                onClose={() => setFormModalOpen(false)}
                onSave={handleSaveEmployee}
                employee={editingEmployee}
            />
        </div >
    );
};

export default AdminDashboard;
